import React, { useState, useRef, useContext, forwardRef } from 'react';
import {
    PopoverBase,
    PopoverBaseProps,
    Drawer,
    useBreakpoint,
} from '@landr/maestro';
import { noop } from 'utils/noop';
import { TrackingContext } from 'contexts/TrackingContext/TrackingContext';
import CollectionsListPopover, {
    CollectionsListPopoverProps,
} from 'components/CollectionsListPopover';
import { AnalyticsSource, Sample } from 'types';

export function useOpenAddToCollectionPrompt(): {
    openAddToCollection: (
        samples: Sample[],
        source: AnalyticsSource,
        placeholder?: string,
    ) => void;
    AddToCollection: React.FC<
        PopoverBaseProps & {
            samples: Sample[];
            placeholder?: string;
        }
    >;
    anchorRef: React.MutableRefObject<null>;
    CollectionsList: React.ForwardRefExoticComponent<
        CollectionsListPopoverProps &
            React.RefAttributes<HTMLDivElement> & {
                samples: Sample[];
                placeholder?: string;
            }
    >;
} {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const { getSampleCollectionInitializedTracker } =
        useContext(TrackingContext);

    const openAddToCollection = (
        samples: Sample[],
        source: AnalyticsSource,
    ): void => {
        setIsOpen(true);

        getSampleCollectionInitializedTracker({
            samples,
            ...source,
        })();
    };

    const handleOnClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
    };
    const isMobile = useBreakpoint('md');

    const CollectionsList = forwardRef(
        (
            {
                ...props
            }: CollectionsListPopoverProps & {
                samples: Sample[];
                placeholder?: string;
            },
            ref: React.Ref<HTMLDivElement>,
        ) => {
            const sampleIds: string[] = props.samples.map(
                (sample: Sample) => sample?.guid,
            );

            return (
                <CollectionsListPopover
                    ref={ref}
                    sampleIds={sampleIds}
                    placeholder={props.placeholder}
                    onClick={handleOnClick}
                    {...props}
                />
            );
        },
    );

    const close = (): void => setIsOpen(false);

    const AddToCollectionDrawer: React.FC<{
        samples: Sample[];
        placeholder?: string;
    }> = ({ ...props }) => (
        <Drawer isVisible={isOpen} onClose={close}>
            <Drawer.Content>
                <CollectionsList hide={noop} {...props} />
            </Drawer.Content>
        </Drawer>
    );

    const AddToCollectionPopover: React.FC<
        PopoverBaseProps & {
            samples: Sample[];
            placeholder?: string;
        }
    > = ({ ...props }) => (
        <PopoverBase
            isOpen={isOpen}
            onClickOutside={close}
            py={0}
            px={0}
            {...props}
        >
            <CollectionsList hide={close} {...props} />
        </PopoverBase>
    );

    const AddToCollection: React.FC<
        PopoverBaseProps & {
            samples: Sample[];
            placeholder?: string;
        }
    > = ({ ...props }) => {
        return (
            <>
                {isMobile && <AddToCollectionDrawer samples={props.samples} />}
                {!isMobile && <AddToCollectionPopover {...props} />}
            </>
        );
    };

    return { openAddToCollection, AddToCollection, anchorRef, CollectionsList };
}
